/**
 * SkipContentHandler
 *
 * Handles accessibility "skip to content" navigation links.
 *
 * Features:
 * - Intercepts anchor links pointing to in-page IDs (#section)
 * - Moves focus to the target element for accessibility compliance
 * - Automatically opens Bootstrap offcanvas components when needed
 * - Ensures the target becomes focusable if not already
 *
 * Usage:
 * Add `data-controller="skip-content"` to a <nav> container
 * containing skip links (anchors with href="#target-id").
 *
 * Example:
 * <nav data-controller="skip-content">
 *     <a href="#main-content">Skip to content</a>
 * </nav>
 */
import "../scss/SkipContentHandler.scss";

class SkipContentHandler {

    /**
     * Initialize the handler on a navigation container.
     *
     * @param {HTMLElement} navElement
     */
    init(navElement) {
        if (!navElement) return;

        navElement
            .querySelectorAll('a[href^="#"]')
            .forEach((link) => {
                link.addEventListener('click', this.handleClick.bind(this));
            });
    }

    /**
     * Handle skip-link click behavior.
     *
     * @param {MouseEvent} e
     */
    handleClick(e) {
        const link = e.currentTarget;
        const href = link.getAttribute('href');
        const target = document.querySelector(href);

        if (!target) return;

        // If link targets an offcanvas component
        if (
            link.dataset.skipOffcanvas === 'true' &&
            target.classList.contains('offcanvas')
        ) {
            this.handleOffcanvas(target);
            return;
        }

        this.focusTarget(target);
    }

    /**
     * Handle Bootstrap offcanvas opening + focus management.
     *
     * @param {HTMLElement} target
     */
    handleOffcanvas(target) {
        try {
            const oc = bootstrap.Offcanvas.getOrCreateInstance(target);
            oc.show();

            target.addEventListener('shown.bs.offcanvas', function onShow() {
                target.removeEventListener('shown.bs.offcanvas', onShow);

                const focusable = target.querySelector(
                    '[tabindex],button,a,input,select,textarea,[role="button"]'
                );

                (focusable || target).focus();
            }, { once: true });

        } catch (err) {
            // Fallback if Bootstrap is unavailable
            target.setAttribute('tabindex', '-1');
            target.focus({ preventScroll: true });
        }
    }

    /**
     * Move focus to a target element, ensuring it is focusable.
     *
     * @param {HTMLElement} target
     */
    focusTarget(target) {
        setTimeout(() => {
            if (!target.hasAttribute('tabindex')) {
                target.setAttribute('tabindex', '-1');
            }
            target.focus({ preventScroll: true });
        }, 0);
    }
}

export default SkipContentHandler;